import { useState } from "react";
import { Button, message } from "antd";
import { WarningOutlined } from "@ant-design/icons";
import DataKitApi from "../../../../api/DataKitApi";
import { DataKitDto, DataKitPublishDiffDto } from "../../../../domain/DataKitDto";
import { DataKitReviewChanges } from "./DataKitReviewChanges";
import "./DataKitPublishBar.scss";

interface Props {
  kit: DataKitDto;
  diff: DataKitPublishDiffDto;
  canWrite: boolean;
  onDone: () => void;
  /** Review changes' Edit - opens the item drawer. */
  onEditItem: (itemId: string) => void;
}

/**
 * The unpublished banner: how many items have changes waiting, Review changes, and Publish to
 * designs (Oct 12). Review changes opens the list right under the banner; there is no Discard-all
 * here any more - discarding is per item, on Review changes' rows, so one change can go without
 * taking the others with it.
 */
export function DataKitPublishBar({ kit, diff, canWrite, onDone, onEditItem }: Props) {
  const [busy, setBusy] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);

  if (diff.totalChanges === 0) return null;

  // Items and groups, not fields - one item with three edits is one change waiting. Anything off
  // every design isn't counted at all: under the staging rule it applied when it was saved, so a
  // kit no location uses never shows this bar, and the "not assigned yet" variant it used to carry
  // has nothing left to describe.
  const itemCount = diff.itemCount ?? 0;
  const groupCount = diff.groupCount ?? 0;
  const designCount = diff.designCount ?? 0;
  const lead = waitingSentence(itemCount, groupCount);
  const follow =
    designCount > 0
      ? `${designCount} design${designCount === 1 ? "" : "s"} still show${designCount === 1 ? "s" : ""} the last published version.`
      : "Designs still show the last published version.";

  const publish = () => {
    if (!kit.id) return;
    setBusy(true);
    DataKitApi.publishKit(kit.id)
      .then((res) => {
        // The banner's own numbers, not r.itemsPublished: publish copies every row in the kit, so
        // that count is the kit's size, and "Published 389 items" after changing one is noise.
        message.success(
          designCount > 0
            ? `Published to ${designCount} design${designCount === 1 ? "" : "s"}`
            : "Published"
        );
        onDone();
      })
      .catch(() => message.error("Could not publish this library"))
      .finally(() => setBusy(false));
  };

  return (
    <>
      <div className="data-kit-publish-bar">
        <WarningOutlined className="data-kit-publish-bar-icon" />
        <p className="data-kit-publish-bar-copy">
          <strong>{lead}</strong> <span>{follow}</span>{" "}
          <button
            type="button"
            className="data-kit-publish-bar-review"
            onClick={() => setReviewOpen((v) => !v)}
            aria-expanded={reviewOpen}
          >
            {reviewOpen ? "Hide changes" : "Review changes"}
          </button>
        </p>
        {canWrite && (
          <Button type="primary" className="data-kit-publish-bar-btn" onClick={publish} loading={busy}>
            Publish to designs
          </Button>
        )}
      </div>
      {reviewOpen && (
        <DataKitReviewChanges
          kit={kit}
          canWrite={canWrite}
          refreshToken={diff}
          onEditItem={onEditItem}
          onChanged={onDone}
        />
      )}
    </>
  );
}

/**
 * "[N] items and [N] groups have changes waiting to publish." Drops whichever half is zero, and
 * reads "1 item has" / "1 group has" for one - the AC's exact grammar.
 */
function waitingSentence(items: number, groups: number): string {
  const parts: string[] = [];
  if (items > 0) parts.push(`${items} item${items === 1 ? "" : "s"}`);
  if (groups > 0) parts.push(`${groups} group${groups === 1 ? "" : "s"}`);
  const plural = items + groups !== 1 || parts.length > 1;
  return `${parts.join(" and ")} ${plural ? "have" : "has"} changes waiting to publish.`;
}
